import { useState, useEffect } from 'react';
import { Users, BookOpen, TrendingUp, Activity } from 'lucide-react';
import adminService from '../../services/adminService';
import LoadingSpinner from '../ui/LoadingSpinner';
import Alert from '../ui/Alert';
import Card from '../ui/Card';

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const result = await adminService.getAnalytics();
      
      if (!result.success) {
        throw new Error(result.error);
      }

      setAnalytics(result.analytics);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  if (!analytics) {
    return <Alert variant="info">No analytics data available</Alert>;
  }

  const stats = [
    {
      label: 'Total Users',
      value: analytics.userMetrics?.totalUsers || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      label: 'Active Users (30d)',
      value: analytics.userMetrics?.activeUsers || 0,
      icon: Activity,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      label: 'Total Workshops',
      value: analytics.ceuMetrics?.totalWorkshops || 0,
      icon: BookOpen,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      label: 'Total CEUs',
      value: (analytics.ceuMetrics?.totalCEUHours || 0).toFixed(1),
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">System Analytics</h2>
        <p className="text-gray-600 mt-1">Overview of system usage and metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Breakdown</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Admin Users</span>
              <span className="font-medium text-gray-900">
                {analytics.userMetrics?.adminUsers || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Regular Users</span>
              <span className="font-medium text-gray-900">
                {(analytics.userMetrics?.totalUsers || 0) - (analytics.userMetrics?.adminUsers || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active Last 30 Days</span>
              <span className="font-medium text-gray-900">
                {analytics.userMetrics?.activeUsers || 0}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">CEU Metrics</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Workshops</span>
              <span className="font-medium text-gray-900">
                {analytics.ceuMetrics?.totalWorkshops || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total CEUs Tracked</span>
              <span className="font-medium text-gray-900">
                {(analytics.ceuMetrics?.totalCEUHours || 0).toFixed(1)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Avg CEUs per User</span>
              <span className="font-medium text-gray-900">
                {analytics.userMetrics?.totalUsers > 0
                  ? ((analytics.ceuMetrics?.totalCEUHours || 0) / analytics.userMetrics.totalUsers).toFixed(1)
                  : '0.0'}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* System Health */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">User Engagement</span>
              <span className="text-sm font-medium text-gray-900">
                {analytics.userMetrics?.totalUsers > 0
                  ? Math.round((analytics.userMetrics.activeUsers / analytics.userMetrics.totalUsers) * 100)
                  : 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full"
                style={{
                  width: `${analytics.userMetrics?.totalUsers > 0
                    ? (analytics.userMetrics.activeUsers / analytics.userMetrics.totalUsers) * 100
                    : 0}%`
                }}
              />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              {analytics.userMetrics?.activeUsers > 0 ? (
                <>
                  <span className="font-medium text-green-600">✓ Healthy</span> - 
                  Users are actively tracking their professional development
                </>
              ) : (
                <>
                  <span className="font-medium text-yellow-600">⚠ Low Activity</span> - 
                  Consider promoting the platform to increase engagement
                </>
              )}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
